// IndexedDB utility for storing app data
class AppDataDB {
  private dbName = 'carthago-app-data'
  private version = 2
  private db: IDBDatabase | null = null
  private readonly REFRESH_INTERVAL = 6 * 60 * 60 * 1000 // 6 hours in milliseconds

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Create stores for each data type
        if (!db.objectStoreNames.contains('destinations')) {
          db.createObjectStore('destinations', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('restaurants')) {
          db.createObjectStore('restaurants', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('hotels')) {
          db.createObjectStore('hotels', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('activities')) {
          db.createObjectStore('activities', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('museums')) {
          db.createObjectStore('museums', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' })
        }
      }
    })
  }

  async storeData(storeName: string, data: any[]): Promise<void> {
    if (!this.db) await this.init()
    
    const transaction = this.db!.transaction([storeName, 'metadata'], 'readwrite')
    const store = transaction.objectStore(storeName)
    const metaStore = transaction.objectStore('metadata')
    
    // Clear existing data
    await store.clear()
    
    // Add new data
    for (const item of data) {
      await store.add(item)
    }
    
    // Update timestamp
    await metaStore.put({ key: `${storeName}_timestamp`, value: Date.now() })
  }

  async isDataStale(storeName: string): Promise<boolean> {
    if (!this.db) await this.init()
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(['metadata'], 'readonly')
      const store = transaction.objectStore('metadata')
      const request = store.get(`${storeName}_timestamp`)
      
      request.onsuccess = () => {
        const result = request.result
        if (!result) {
          resolve(true) // No timestamp means data is stale
          return
        }
        
        const lastUpdate = result.value
        const now = Date.now()
        const isStale = (now - lastUpdate) > this.REFRESH_INTERVAL
        resolve(isStale)
      }
      
      request.onerror = () => resolve(true) // Error means treat as stale
    })
  }

  async getData(storeName: string): Promise<any[]> {
    if (!this.db) await this.init()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.getAll()
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  async searchAll(query: string): Promise<any[]> {
    const results: any[] = []
    const stores = ['destinations', 'restaurants', 'hotels', 'activities', 'museums']
    
    for (const storeName of stores) {
      const data = await this.getData(storeName)
      const filtered = data.filter(item => {
        const searchQuery = query.toLowerCase()
        
        // Search in localized names
        const nameMatches = 
          item.name?.toLowerCase().includes(searchQuery) ||
          item.name_en?.toLowerCase().includes(searchQuery) ||
          item.name_fr?.toLowerCase().includes(searchQuery) ||
          item.name_ar?.toLowerCase().includes(searchQuery) ||
          item.title?.toLowerCase().includes(searchQuery) ||
          item.title_en?.toLowerCase().includes(searchQuery) ||
          item.title_fr?.toLowerCase().includes(searchQuery) ||
          item.title_ar?.toLowerCase().includes(searchQuery)
        
        // Search in descriptions
        const descriptionMatches = 
          item.description?.toLowerCase().includes(searchQuery) ||
          item.description_en?.toLowerCase().includes(searchQuery) ||
          item.description_fr?.toLowerCase().includes(searchQuery) ||
          item.description_ar?.toLowerCase().includes(searchQuery) ||
          item.short_description?.toLowerCase().includes(searchQuery) ||
          item.short_description_en?.toLowerCase().includes(searchQuery) ||
          item.short_description_fr?.toLowerCase().includes(searchQuery) ||
          item.short_description_ar?.toLowerCase().includes(searchQuery)
        
        return nameMatches || descriptionMatches
      })
      results.push(...filtered.map(item => ({ ...item, type: storeName })))
    }
    
    return results
  }

  async clearAllData(): Promise<void> {
    if (!this.db) await this.init()
    
    const stores = ['destinations', 'restaurants', 'hotels', 'activities', 'museums', 'metadata']
    const transaction = this.db!.transaction(stores, 'readwrite')
    
    for (const storeName of stores) {
      const store = transaction.objectStore(storeName)
      await store.clear()
    }
  }
}

export const appDataDB = new AppDataDB()